import { type ReactNode, forwardRef } from 'react';

import * as styles from './BaseInputContent.sty.js';
import { TextEllipsisAroundChildren } from '../../core/components/TextEllipsisAroundChildren.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { Flex } from '../../layouts/flex/Flex.js';

/**
 * Accepted properties for the Content
 * @public
 */
export interface BaseInputContentProps extends BehaviorTrackingProps {
  /** Elements to be displayed in the Prefix slot. */
  children: ReactNode;
}

/*
 * The BaseInputContent does not expose className / style, because it is only used to determine
 * whether the children should be rendered as prefix or suffix items. The BaseInputContent is then
 * internally used again to wrap only the prefix elements, but not the prefix buttons, which are
 * rendered next to the input container.
 * Adding custom styles would only style the elements wrapped in the internally rendered BaseInputContent
 * container, which would be unexpected, since for the user, the buttons are also wrapped in the container.
 */

/**
 * BaseInputContent component, used for adding elements to the prefix/suffix slot.
 */
const BaseInputContent = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  BaseInputContentProps
>((props, forwardedRef) => {
  const { children, ...rest }: BaseInputContentProps = props;

  const [behavioralTrackingProps] = useBehavioralTrackingProps(rest);

  return (
    <Flex
      ref={forwardedRef}
      flexGrow={0}
      alignItems="center"
      alignSelf="stretch"
      gap={8}
      paddingLeft={8}
      paddingRight={8}
      className={styles.content}
      {...behavioralTrackingProps}
    >
      <TextEllipsisAroundChildren>{children}</TextEllipsisAroundChildren>
    </Flex>
  );
});

/**
 * Component used for adding elements to the prefix slot.
 * @internal
 */
export const BaseInputPrefix = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  BaseInputContentProps
>((props, forwardedRef) => <BaseInputContent ref={forwardedRef} {...props} />);

/**
 * Component used for adding elements to the suffix slot.
 * @internal
 */
export const BaseInputSuffix = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  BaseInputContentProps
>((props, forwardedRef) => <BaseInputContent ref={forwardedRef} {...props} />);

/**
 * Component used for adding elements to the reserved suffix slot.
 * @internal
 */
export const BaseInputReservedSuffix = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  BaseInputContentProps
>((props, forwardedRef) => <BaseInputContent ref={forwardedRef} {...props} />);

(
  BaseInputPrefix as typeof BaseInputPrefix & { displayName: string }
).displayName = 'Input.Prefix';
(
  BaseInputSuffix as typeof BaseInputSuffix & { displayName: string }
).displayName = 'Input.Suffix';
